import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import Layout from "./components/Layout";
import InvoiceGenerator from "./pages/InvoiceGenerator";
import FileDifferenceChecker from "./pages/FileDifferenceChecker";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import RefundPolicy from "./pages/RefundPolicy";
import "./App.css";
import "./components/css/PromoBanner.css";
import RazorpayCheckout from "./pages/RazorpayCheckout";

const AppContent = ({ setNavProps }) => {
  const [showContactForm, setShowContactForm] = useState(true);
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [showPricingSection, setShowPricingSection] = useState(true);
  const [activeSection, setActiveSection] = useState("home");
  const [showPopup, setShowPopup] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");

  const navigate = useNavigate();

  const homeRef = useRef(null);
  const productsRef = useRef(null);
  const solutionsRef = useRef(null);
  const featuresRef = useRef(null);
  const pricingRef = useRef(null);
  const testimonialsRef = useRef(null);
  const supportRef = useRef(null);
  const aboutRef = useRef(null);
  const contactRef = useRef(null);
  

  const handleNavigate = () => {
    navigate("/razorpaycheckout");
  };

  const metaDescription =
    "FREE Invoice Generator Online - Create professional invoices instantly! No sign-up required. GST support, PDF download, WhatsApp sharing. Also offering best restaurant POS & inventory management software in India.";
  const pageTitle =
    "Free Invoice Generator Online | Professional Invoicing Tool India | Dhandha Karle";

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Dhandha Karle",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web, Mobile",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
      "description":
        "Advanced Restaurant POS, Inventory Management, and Free Invoice Generator Software for businesses in India",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "1000",
      },
    });
    document.head.appendChild(script);

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 50;
      const sections = [
        { ref: homeRef, name: "home" },
        { ref: productsRef, name: "products" },
        { ref: solutionsRef, name: "solutions" },
        { ref: featuresRef, name: "features" },
        { ref: pricingRef, name: "pricing" },
        { ref: testimonialsRef, name: "testimonials" },
        { ref: supportRef, name: "support" },
        { ref: aboutRef, name: "about" },
        { ref: contactRef, name: "contact" },
      ];

      let currentSection = activeSection;
      for (const section of sections) {
        if (section.ref.current) {
          const top = section.ref.current.offsetTop;
          const bottom = top + section.ref.current.offsetHeight;
          if (scrollPosition >= top && scrollPosition < bottom) {
            currentSection = section.name;
            break;
          }
        }
      }
      setActiveSection(currentSection);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      document.head.removeChild(script);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeSection]);

  useEffect(() => {
    setNavProps({
      activeSection,
      scrollToSection: (ref, section) => {
        setActiveSection(section);
        scrollToSection(ref, section);
      },
      refs: {
        home: homeRef,
        products: productsRef,
        solutions: solutionsRef,
        features: featuresRef,
        pricing: pricingRef,
        testimonials: testimonialsRef,
        support: supportRef,
        about: aboutRef,
        contact: contactRef,
      },
    });
  }, [activeSection, setNavProps]);

  const scrollToSection = (ref, section) => {
    setActiveSection(section);
    if (!ref.current) return;
    const offset = -100;
    const elementPosition =
      ref.current.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({
      top: elementPosition + offset,
      behavior: "smooth",
    });
  };

  const scrollToContact = () => {
    scrollToSection(contactRef, "contact");
  };

  const toggleDemoForm = () => {
    setShowDemoForm(!showDemoForm);
    if (!showDemoForm) {
      scrollToSection(contactRef, "contact");
    }
  };

  const togglePricingSection = () => {
    setShowPricingSection(true);
    scrollToSection(pricingRef, "pricing");
  };

  const goToInvoiceGenerator = () => {
    navigate("/invoice-generator");
  };

  const goToFileDiffChecker = () => {
    navigate("/file-difference-checker");
  };

  const sendEmail = (e, formType) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);

    if (formType === "Phone") {
      data.message = `Phone Number Popup: ${data.phone}`;
      data.name = "Popup User";
      data.email = "popup@dhandhakarle.com";
    }

    emailjs
      .send(
        "service_qxfzblh",
        "template_543gl6n",
        data,
        "55rDJxZlYVITGi3zR"
      )
      .then(
        (result) => {
          alert(`${formType} submitted successfully!`);
          e.target.reset();
          if (formType === "Phone") {
            setPhoneNumber("");
            setShowPopup(false);
          }
        },
        (error) => {
          alert("Failed to send the form. Please try again.");
          console.log(error.text);
        }
      );
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta
          name="keywords"
          content="free invoice generator, invoice maker online, create invoice free, GST invoice generator, PDF invoice creator, professional invoice tool, invoice generator India, free billing software, online invoicing, WhatsApp invoice, restaurant POS, inventory management, business software India, Dhandha Karle"
        />
        <meta name="author" content="Dhandha Karle Team" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dhandhakarle.com" />
        <meta
          property="og:image"
          content="https://dhandhakarle.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href="https://dhandhakarle.com" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      {showPopup && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="popup-overlay"
        >
          <div className="popup-card">
            <h3>Get Started with Dhandha Karle</h3>
            <p>Enter your phone number for a personalized demo.</p>
            <form onSubmit={(e) => sendEmail(e, "Phone")}>
              <input
                type="tel"
                name="phone"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Your Phone Number"
                required
                className="popup-input"
              />
              <div className="popup-buttons">
                <button type="submit" className="popup-submit">
                  Submit
                </button>
                <button
                  onClick={() => setShowPopup(false)}
                  className="popup-cancel"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      <section ref={homeRef} className="hero">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2>Grow Smarter with Dhandha Karle</h2>
          <p>
            Empower your restaurant or business with cutting-edge POS, inventory
            management, and free invoice generation tools.
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              justifyContent: "center",
            }}
          >
            <button
              onClick={toggleDemoForm}
              className="cta-button"
              style={{ marginBottom: "0.5rem" }}
            >
              Request a Free Demo
            </button>
            <button
              onClick={goToInvoiceGenerator}
              className="cta-button secondary"
              style={{ marginBottom: "0.5rem" }}
            >
              Try Free Invoice Generator
            </button>
          {/*  <button
              onClick={togglePricingSection}
              className="cta-button tertiary"
              style={{ marginBottom: "0.5rem" }}
            >
              View Pricing
            </button>*/}
          </div>
        </motion.div>
      </section>

      {/* Invoice Generator Promotional Banner */}
      <motion.section 
        className="invoice-promo-banner"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="promo-content">
          <div className="promo-badge">100% FREE</div>
          <motion.h2
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Free Professional Invoice Generator
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            Create unlimited professional invoices instantly - No sign-up required! GST Support, PDF Download, WhatsApp Sharing - Completely FREE Forever
          </motion.p>
          <div className="promo-features">
            <div className="promo-feature">Unlimited Invoices</div>
            <div className="promo-feature">GST Compliant</div>
            <div className="promo-feature">Instant PDF</div>
            <div className="promo-feature">No Hidden Charges</div>
          </div>
          <motion.button
            onClick={goToInvoiceGenerator}
            className="promo-cta-button"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 25px 60px rgba(245, 158, 11, 0.6)"
            }}
            whileTap={{ scale: 0.95 }}
            viewport={{ once: true }}
          >
            Create Free Invoice Now
          </motion.button>
          <p className="promo-note">Join 10,000+ businesses using our free invoice generator</p>
        </div>
      </motion.section>

      <section ref={productsRef} className="section products">
        <motion.h2
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          viewport={{ once: true }}
        >
          Our Products
        </motion.h2>
        <div className="grid">
          {[
            {
              title: "Restaurant POS Software",
              desc: "Streamline orders, billing, and kitchen workflows with our intuitive POS system tailored for all types of restaurants.",
              features: [
                "Fast Order Processing",
                "Table & KOT Management",
                "Online Order Integration",
                "Staff Performance Tracking",
              ],
              bg: "#dbeafe",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="2" width="20" height="20" rx="2" ry="2"></rect>
                  <line x1="6" y1="6" x2="18" y2="6"></line>
                  <line x1="6" y1="10" x2="18" y2="10"></line>
                  <line x1="6" y1="14" x2="14" y2="14"></line>
                  <circle cx="18" cy="18" r="2"></circle>
                </svg>
              ),
            },
            {
              title: "Inventory Management System",
              desc: "Take control of your stock with real-time tracking, automated reordering, and detailed analytics for smarter decisions.",
              features: [
                "Stock Alerts & Expiry Tracking",
                "Multi-Warehouse Support",
                "Supplier Management",
                "Barcode Integration",
              ],
              bg: "#fef3c7",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 7h-16a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-10a2 2 0 0 0-2-2z"></path>
                  <path d="M4 7v-4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"></path>
                  <line x1="8" y1="12" x2="16" y2="12"></line>
                  <line x1="8" y1="16" x2="12" y2="16"></line>
                </svg>
              ),
            },
            {
              title: "Invoice Generator",
              desc: "Generate unlimited invoices for free with a simple, elegant tool designed for all businesses.",
              features: [
                "Free Unlimited Invoices",
                "Zero Cost",
                "WhatsApp Sharing",
                "Simple & Elegant Design",
              ],
              bg: "#e0f2fe",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16v16H4z"></path>
                  <path d="M8 8h8m-8 4h8m-8 4h4"></path>
                </svg>
              ),
            },
          ].map((product, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.6, 
                delay: idx * 0.15,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
              viewport={{ once: true }}
              className="card"
              style={{ backgroundColor: product.bg }}
            >
              {product.icon}
              <h3>{product.title}</h3>
              <p>{product.desc}</p>
              <ul>
                {product.features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      <section ref={solutionsRef} className="section solutions">
        <motion.h2
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          viewport={{ once: true }}
        >
          Tailored Business Solutions
        </motion.h2>
        <div className="grid">
          {[
            {
              title: "Restaurants",
              desc: "Boost efficiency in quick-service, cafes, or fine dining.",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="2"
                >
                  <path d="M12 2v6m-6 4h12m-6 4v6m-6-10a6 6 0 0 1 12 0" />
                </svg>
              ),
            },
            {
              title: "Retail Stores",
              desc: "Simplify billing and stock management for retail.",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                >
                  <path d="M6 2h12l2 6h-16l2-6zm-2 6h16v12a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2v-12z" />
                </svg>
              ),
            },
            {
              title: "Wholesalers",
              desc: "Manage bulk inventory and supply chains.",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2"
                >
                  <path d="M3 3h18v6h-18v-6zm0 8h18v10h-18v-10z" />
                </svg>
              ),
            },
            {
              title: "Franchises",
              desc: "Centralize operations across locations.",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#8b5cf6"
                  strokeWidth="2"
                >
                  <path d="M12 2a10 10 0 0 0-10 10 10 10 0 0 0 10 10 10 10 0 0 0 10-10 10 10 0 0 0-10-10zm0 4v12" />
                </svg>
              ),
            },
          ].map((solution, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50, rotateY: -15 }}
              whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ 
                duration: 0.7, 
                delay: idx * 0.1,
                type: "spring",
                stiffness: 80
              }}
              whileHover={{ 
                y: -10,
                rotateY: 5,
                transition: { duration: 0.3 }
              }}
              viewport={{ once: true }}
              className="solution-card"
            >
              {solution.icon}
              <h3>{solution.title}</h3>
              <p>{solution.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section ref={featuresRef} className="section features">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          viewport={{ once: true }}
        >
          Why Choose Dhandha Karle?
        </motion.h2>
        <div className="grid">
          {[
            {
              title: "User-Friendly Design",
              desc: "Intuitive interface for quick onboarding.",
              bg: "#e0f2fe",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="2"
                >
                  <path d="M12 2a10 10 0 0 0-10 10 10 10 0 0 0 10 10 10 10 0 0 0 10-10 10 10 0 0 0-10-10zm-2 7h4v6h-4z" />
                </svg>
              ),
            },
            {
              title: "Advanced Analytics",
              desc: "Real-time insights into sales and stock.",
              bg: "#fce7f3",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#db2777"
                  strokeWidth="2"
                >
                  <path d="M3 15v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4m-14-6h14v6h-14v-6zm4-6h6v6h-6v-6z" />
                </svg>
              ),
            },
            {
              title: "Cross-Platform Access",
              desc: "Run your business from any device.",
              bg: "#ecfccb",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#84cc16"
                  strokeWidth="2"
                >
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M2 8h20" />
                </svg>
              ),
            },
            {
              title: "24/7 Expert Support",
              desc: "Round-the-clock assistance.",
              bg: "#fee2e2",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="2"
                >
                  <path d="M12 2a10 10 0 0 0-10 10 10 10 0 0 0 10 10 10 10 0 0 0 10-10 10 10 0 0 0-10-10zm0 4v4m0 4h.01" />
                </svg>
              ),
            },
            {
              title: "Customizable Workflows",
              desc: "Adapt to your unique needs.",
              bg: "#f3e8ff",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#8b5cf6"
                  strokeWidth="2"
                >
                  <path d="M12 2v6m-6 4h12m-6 4v6" />
                </svg>
              ),
            },
            {
              title: "Secure Data",
              desc: "Bank-grade security for your data.",
              bg: "#dcfce7",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2"
                >
                  <path d="M12 2a10 10 0 0 0-10 10 10 10 0 0 0 10 10 10 10 0 0 0 10-10 10 10 0 0 0-10-10zm-2 7l3 3 5-5" />
                </svg>
              ),
            },
          ].map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.8, rotateZ: -5 }}
              whileInView={{ opacity: 1, scale: 1, rotateZ: 0 }}
              transition={{ 
                duration: 0.5, 
                delay: idx * 0.08,
                type: "spring",
                stiffness: 120
              }}
              whileHover={{ 
                scale: 1.05,
                rotateZ: 2,
                transition: { duration: 0.2 }
              }}
              viewport={{ once: true }}
              className="card"
              style={{ backgroundColor: feature.bg }}
            >
              {feature.icon}
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {showPricingSection && (
        <section ref={pricingRef} className="section pricing">
          <motion.h2
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
            viewport={{ once: true }}
          >
            Pricing Plans
          </motion.h2>

          <h3
            style={{
              textAlign: "center",
              marginBottom: "2rem",
              color: "#ffffff",
              fontSize: "1.8rem",
              fontWeight: "700",
              textShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
            }}
          >
            Restaurant / Inventory POS Software
          </h3>
          <div className="grid">
            {[
              {
                title: "Free Trial",
                price: "",
                desc: "Experience core features for 15 days.",
                features: [
                  "Basic POS Functionality",
                  "Inventory Tracking (Single Location)",
                  "Standard Reports",
                  "Email Support",
                  "1 User",
                ],
                buttonText: "Start Trial",
              },
              {
                title: "Premium Plan",
                price: "₹4999/year",
                desc: "Unlock advanced tools for growing businesses.",
                features: [
                  "Full POS Suite (Online Orders, KOT)",
                  "Multi-Location Inventory",
                  "Advanced Analytics & Custom Reports",
                  "24/7 Phone & Chat Support",
                  "Unlimited Users",
                ],
                buttonText: "Get Premium",
              },
            ].map((plan, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ 
                  duration: 0.6, 
                  delay: idx * 0.2,
                  type: "spring",
                  stiffness: 90
                }}
                whileHover={{ 
                  scale: 1.05,
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                viewport={{ once: true }}
                className="card"
              >
                <h3>{plan.title}</h3>
                {plan.price && <p className="price">{plan.price}</p>}
                <p>{plan.desc}</p>
                <ul>
                  {plan.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
                <button onClick={scrollToContact} className="cta-button">
                  {plan.buttonText}
                </button>
              </motion.div>
            ))}
          </div>

          <motion.h3
            style={{
              textAlign: "center",
              marginTop: "3rem",
              marginBottom: "2rem",
              color: "#ffffff",
              fontSize: "1.8rem",
              fontWeight: "700",
              textShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
            }}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Invoice Generator
          </motion.h3>
          <div className="grid">
            {[
              {
                title: "Free Plan",
                price: "Free Usage",
                desc: "Generate unlimited invoices at no cost.",
                features: [
                  "Unlimited Invoice Generation",
                  "Basic Invoice Features",
                  "WhatsApp Sharing",
                ],
                buttonText: "Start Free",
              },
              {
                title: "Premium Plan",
                price: "₹399 for 6 months or half a year",
                desc: "Enhanced features with invoice record management.",
                features: [
                  "Unlimited Invoice Generation",
                  "Invoice Records Management",
                  "Priority Support",
                ],
                buttonText: "Get Premium",
              },
            ].map((plan, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ 
                  duration: 0.6, 
                  delay: idx * 0.2,
                  type: "spring",
                  stiffness: 90
                }}
                whileHover={{ 
                  scale: 1.08,
                  y: -10,
                  boxShadow: "0 25px 70px rgba(0, 0, 0, 0.3)",
                  transition: { duration: 0.3 }
                }}
                viewport={{ once: true }}
                className="card"
              >
                <h3>{plan.title}</h3>
                <p className="price">{plan.price}</p>
                <p>{plan.desc}</p>
                <ul>
                  {plan.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
                <button onClick={handleNavigate} className="cta-button">
                  {plan.buttonText}
                </button>
              </motion.div>
            ))}
          </div>

          <motion.p 
            className="custom-plan-note"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            Need more? Customized plans available after your trial—contact us!
          </motion.p>
        </section>
      )}

      <section ref={testimonialsRef} className="section testimonials">
        <motion.h2
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          viewport={{ once: true }}
        >
          Trusted by 1000+ Happy Customers
        </motion.h2>
        <div className="grid">
          {[
            {
              quote: "Doubled our order speed with their POS!",
              author: "Ravi, Cafe Delight",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="2"
                >
                  <path d="M5 13l4 4 10-10" />
                </svg>
              ),
            },
            {
              quote: "Inventory tracking saved us hours daily.",
              author: "Priya, Gupta Traders",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                >
                  <path d="M5 13l4 4 10-10" />
                </svg>
              ),
            },
            {
              quote: "Best investment for our chain.",
              author: "Amit, Food Haven",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2"
                >
                  <path d="M5 13l4 4 10-10" />
                </svg>
              ),
            },
            {
              quote: "Support team is always there for us.",
              author: "Sneha, Quick Bites",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="2"
                >
                  <path d="M5 13l4 4 10-10" />
                </svg>
              ),
            },
            {
              quote: "Simplified our franchise operations.",
              author: "Vikram, Taste Buds",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#8b5cf6"
                  strokeWidth="2"
                >
                  <path d="M5 13l4 4 10-10" />
                </svg>
              ),
            },
            {
              quote: "Affordable and powerful software.",
              author: "Neha, Spicy Corner",
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#db2777"
                  strokeWidth="2"
                >
                  <path d="M5 13l4 4 10-10" />
                </svg>
              ),
            },
          ].map((testimonial, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: idx % 2 === 0 ? -80 : 80, scale: 0.9 }}
              whileInView={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ 
                duration: 0.7, 
                delay: idx * 0.1,
                type: "spring",
                stiffness: 80
              }}
              whileHover={{ 
                scale: 1.05,
                rotate: idx % 2 === 0 ? 2 : -2,
                transition: { duration: 0.3 }
              }}
              viewport={{ once: true }}
              className="card"
            >
              {testimonial.icon}
              <p className="quote">"{testimonial.quote}"</p>
              <p className="author">- {testimonial.author}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section ref={supportRef} className="section support">
        <motion.h2
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          viewport={{ once: true }}
        >
          Unmatched Support
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 80 }}
          whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
          viewport={{ once: true }}
          className="support-card"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="50"
            height="50"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2563eb"
            strokeWidth="2"
            className="support-icon"
          >
            <path d="M12 2a10 10 0 0 0-10 10 10 10 0 0 0 10 10 10 10 0 0 0 10-10 10 10 0 0 0-10-10zm0 4v4m0 4h.01" />
          </svg>
          <p>
            Our dedicated team is available 24/7 to ensure your business thrives.
            Whether it’s a quick query or a complex issue, we provide instant
            assistance via email, phone, or live chat with a personal touch.
          </p>
          <div className="support-contact">
            <p>
              Email:{" "}
              <a href="mailto:support@dhandhakarle.com">
                support@dhandhakarle.com
              </a>
            </p>
          </div>
        </motion.div>
      </section>

      <section ref={aboutRef} className="section about">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          viewport={{ once: true }}
        >
          About Dhandha Karle
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, y: 50, rotateX: -15 }}
          whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 80 }}
          whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
          viewport={{ once: true }}
          className="about-card"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="50"
            height="50"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2563eb"
            strokeWidth="2"
            className="about-icon"
          >
            <path d="M12 2a10 10 0 0 0-10 10 10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2zm0 16a6 6 0 1 1 0-12 6 6 0 0 1 0 12z" />
          </svg>
          <p>
            Founded in 2021, Dhandha Karle emerged from a simple idea: to empower
            small and medium businesses in India with technology that simplifies
            operations and fuels growth. Our mission is to bridge the gap between
            traditional business practices and modern digital solutions, offering
            tools like our advanced POS, inventory management systems, and free
            invoice generator. Based in the heart of India, our team of passionate
            innovators—engineers, designers, and business experts—works tirelessly
            to craft software that’s intuitive, affordable, and impactful.
          </p>
        </motion.div>
      </section>

      <section ref={contactRef} className="section forms">
        {showContactForm && (
          <motion.form
            onSubmit={(e) => sendEmail(e, "Contact")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="form-card"
          >
            <h3>Contact Us</h3>
            <input type="text" name="name" placeholder="Your Name" required />
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              required
            />
            <textarea
              name="message"
              placeholder="Your Message"
              required
            ></textarea>
            <button type="submit" className="cta-button">
              Send Message
            </button>
          </motion.form>
        )}
        {showDemoForm && (
          <motion.form
            onSubmit={(e) => sendEmail(e, "Demo")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="form-card"
          >
            <h3>Request a Demo</h3>
            <input type="text" name="name" placeholder="Your Name" required />
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              required
            />
            <input
              type="tel"
              name="phone"
              placeholder="Your Phone Number"
              required
            />
            <textarea
              name="message"
              placeholder="Tell us about your business"
              required
            ></textarea>
            <button type="submit" className="cta-button">
              Schedule Demo
            </button>
          </motion.form>
        )}
      </section>

      <section className="section free-tools">
        <motion.h2
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          viewport={{ once: true }}
        >
          Free Business Tools
        </motion.h2>
        <div className="grid">
          {[
            {
              title: "Invoice Generator",
              desc: "Create unlimited professional invoices with GST support - 100% Free Forever",
              action: "Create Invoice",
              onClick: goToInvoiceGenerator,
              icon: (
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <path d="M7 7h10M7 12h10M7 17h6"/>
                </svg>
              ),
            },
            {
              title: "File Difference Checker",
              desc: "Compare two files or text instantly - Find differences in code, documents & more",
              action: "Compare Files",
              onClick: goToFileDiffChecker,
              icon: (
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                  <path d="M9 3H5a2 2 0 0 0-2 2v4m0 8v4a2 2 0 0 0 2 2h4m8 0h4a2 2 0 0 0 2-2v-4m0-8V5a2 2 0 0 0-2-2h-4"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                  <line x1="12" y1="8" x2="12" y2="16"/>
                </svg>
              ),
            },
          ].map((tool, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.6, 
                delay: idx * 0.2,
                type: "spring",
                stiffness: 90
              }}
              whileHover={{ 
                scale: 1.05,
                y: -10,
                transition: { duration: 0.3 }
              }}
              viewport={{ once: true }}
              className="card tool-card"
            >
              {tool.icon}
              <h3>{tool.title}</h3>
              <p>{tool.desc}</p>
              <button onClick={tool.onClick} className="cta-button" style={{ marginTop: "1rem" }}>
                {tool.action}
          </button>
        </motion.div>
          ))}
        </div>
      </section>
    </>
  );
};

const App = () => {
  const [navProps, setNavProps] = useState({});

  return (
    <Router>
  <Routes>
    <Route
      path="/"
      element={
        <Layout navProps={navProps}>
          <AppContent setNavProps={setNavProps} />
        </Layout>
      }
    />
    <Route
      path="/invoice-generator"
      element={
        <Layout navProps={navProps}>
          <InvoiceGenerator />
        </Layout>
      }
    />
    <Route
      path="/file-difference-checker"
      element={
        <Layout navProps={navProps}>
          <FileDifferenceChecker />
        </Layout>
      }
    />
    <Route
      path="/privacy"
      element={
        <Layout navProps={navProps}>
          <PrivacyPolicy />
        </Layout>
      }
    />
    <Route
      path="/terms"
      element={
        <Layout navProps={navProps}>
          <TermsOfService />
        </Layout>
      }
    />
    <Route
  path="/refund-policy"
  element={
    <Layout navProps={navProps}>
      <RefundPolicy />
    </Layout>
  }
/>
<Route
  path="razorpaycheckout"
  element={
    <Layout navProps={navProps}>
      <RazorpayCheckout />
    </Layout>
  }
/>
  
  </Routes>
</Router>
  );
};

export default App;